'use client';

/**
 * ListSection — generic entity list for rail cards.
 *
 * Mode4-native parallel of `<ListSection />` under
 * copilot/details/sections/. Used for sparse association lists
 * (suppliers, RFQs referencing a product, etc.) that don't merit
 * the products / collections grid treatment.
 *
 * Each row shows `name` (or whatever string field is present); click
 * selects the entity if it has a semantic_id.
 *
 * @layer presentation
 * @arch_component_type section
 * @arch_domain copilot / rail-insights
 */

import { ChevronRight } from 'lucide-react';
import { useGraphSelectionStore } from '@/lib/stores/graph-selection-store';
import { cn } from '@/lib/utils/cn';
import { text } from '@/lib/design/tokens';

interface ListItem {
  semantic_id?: string;
  name?: string;
  type?: string;
  [k: string]: unknown;
}

interface ListSectionProps {
  items: ListItem[] | null | undefined;
  label?: string | null;
  maxItems?: number | null;
  entityType?: string;
}

export function ListSection({ items, label, maxItems, entityType }: ListSectionProps) {
  const selectNode = useGraphSelectionStore((s) => s.selectNode);  if (!items || items.length === 0) return null;

  const display = maxItems ? items.slice(0, maxItems) : items;
  const overflow = items.length - display.length;

  return (
    <div>
      <p className={text.eyebrow}>
        {label || 'Items'}{' '}
        <span className="text-muted-foreground">({items.length})</span>
      </p>
      <ul className="space-y-0.5">
        {display.map((it, i) => (
          <li key={it.semantic_id ?? i}>
            <button
              onClick={() =>
                it.semantic_id &&
                selectNode({
                  id: it.semantic_id,
                  label: it.name ?? '',
                  type: it.type ?? entityType ?? 'unknown',
                  properties: {},
                })
              }
              className={cn(
                'w-full flex items-center gap-2 px-2 py-1 rounded hover:bg-accent/50 transition-colors text-left',
                text.body,
              )}
            >
              <span className="flex-1 min-w-0 truncate">{it.name ?? '(unnamed)'}</span>
              {it.semantic_id && (
                <ChevronRight className="h-3 w-3 text-muted-foreground/60 shrink-0" />
              )}
            </button>
          </li>
        ))}
      </ul>
      {overflow > 0 && (
        <p className={cn(text.muted, 'mt-1')}>+{overflow} more</p>
      )}
    </div>
  );
}
